import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import {
  approveApplication,
  fetchRegistrations,
  registrationAction,
  sendPaymentLink,
} from './api';
import type { Registration, SuiteFilter } from './types';
import { daysUntil, fmtDate, statusStyle, suiteFromType } from './utils';

const ApplicationsPage: React.FC = () => {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const statusFilter = searchParams.get('status') || 'all';
  const urlFilter = searchParams.get('filter') || 'all';
  const [suiteFilter, setSuiteFilter] = useState<SuiteFilter>('all');
  const [busy, setBusy] = useState<string | null>(null);

  const setStatusFilter = (st: string) => {
    const next = new URLSearchParams(searchParams);
    if (st === 'all') next.delete('status');
    else next.set('status', st);
    setSearchParams(next, { replace: true });
  };

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      setRows(await fetchRegistrations(token));
    } catch {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((r) => {
      const statusMatch = statusFilter === 'all' || r.status === statusFilter;
      const suiteMatch = suiteFilter === 'all' || suiteFromType(r.business_type) === suiteFilter;
      const searchMatch = !q ||
        (r.business_name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.referral_source || '').toLowerCase().includes(q) ||
        (r.referral_detail || '').toLowerCase().includes(q);
      let expiringMatch = true;
      if (urlFilter === 'expiring') {
        const d = daysUntil(r.trial_ends_at);
        expiringMatch = r.status === 'trial' && d !== null && d >= 0 && d <= 3;
      }
      return statusMatch && suiteMatch && searchMatch && expiringMatch;
    });
  }, [rows, search, statusFilter, suiteFilter, urlFilter]);

  const run = async (id: string, action: 'activate' | 'extend' | 'cancel' | 'reject', reason?: string) => {
    if (action === 'cancel' && !window.confirm('Cancel this application and suspend the account?')) return;
    if (action === 'reject' && !window.confirm('Reject this application? The applicant will be notified by email.')) return;
    setBusy(`${id}-${action}`);
    try {
      const result = await registrationAction(token!, id, action, action === 'extend' ? { days: 7 } : reason ? { reason } : undefined);
      if (action === 'reject') {
        if (result.emailSent) toast.success('Application rejected — applicant notified by email');
        else toast.warn(result.emailError || 'Rejected in system but email was not delivered — check applicant address or Azure email');
      } else {
        toast.success(`Application ${action}${action === 'extend' ? 'ed' : 'd'}`);
      }
      await load();
    } catch {
      toast.error(`Failed to ${action}`);
    } finally {
      setBusy(null);
    }
  };

  const approve = async (id: string) => {
    if (!window.confirm('Approve and send 14-day demo access?')) return;
    setBusy(`${id}-approve`);
    try {
      await approveApplication(token!, id, 14);
      toast.success('Application approved — demo access emailed');
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to approve');
    } finally {
      setBusy(null);
    }
  };

  const paymentLink = async (id: string) => {
    setBusy(`${id}-pay`);
    try {
      const res = await sendPaymentLink(token!, id);
      toast.success('Payment link sent');
      if (res.url) window.open(res.url, '_blank');
      await load();
    } catch {
      toast.error('Failed to send payment link');
    } finally {
      setBusy(null);
    }
  };

  const statuses = ['all', 'pending', 'trial', 'active', 'paid', 'payment_sent', 'rejected', 'expired', 'cancelled'];

  return (
    <>
      <div className="plat-header">
        <div>
          <h2>Applications</h2>
          <p>Review applications, approve demos, and manage trials.</p>
        </div>
        <button type="button" className="plat-btn plat-btn-ghost" onClick={load}>Refresh</button>
      </div>

      <div className="plat-toolbar">
        <input
          className="plat-search"
          placeholder="Search business, email or referral…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="plat-pills">
          {(['all', 'landlord', 'wholesale'] as SuiteFilter[]).map((s) => (
            <button
              key={s}
              type="button"
              className={`plat-pill${suiteFilter === s ? ' active' : ''}`}
              onClick={() => setSuiteFilter(s)}
            >{s}</button>
          ))}
        </div>
      </div>

      <div className="plat-pills" style={{ marginBottom: 14 }}>
        {statuses.map((st) => (
          <button
            key={st}
            type="button"
            className={`plat-pill${statusFilter === st ? ' active' : ''}`}
            onClick={() => setStatusFilter(st)}
          >{st}</button>
        ))}
      </div>

      <div className="plat-panel" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="plat-empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="plat-empty">No applications match your filters.</div>
        ) : (
          <div className="plat-table-wrap">
            <table className="plat-table">
              <thead>
                <tr>
                  <th>Business</th>
                  <th>Contact</th>
                  <th>Referral</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Trial ends</th>
                  <th>Left</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const days = daysUntil(r.trial_ends_at);
                  const isBusy = (a: string) => busy === `${r.id}-${a}`;
                  const referralText = r.referral_detail
                    ? `${r.referral_source || '—'} — ${r.referral_detail}`
                    : (r.referral_source || '—');
                  return (
                    <tr key={r.id}>
                      <td>
                        <div style={{ fontWeight: 700 }}>{r.business_name}</div>
                        <div className="plat-muted">{r.business_type || suiteFromType(r.business_type)}</div>
                        {r.company_number && (
                          <div className="plat-muted" style={{ fontSize: 11 }}>Co. {r.company_number}</div>
                        )}
                        {r.application_note && (
                          <div className="plat-muted" style={{ fontSize: 11, marginTop: 4, maxWidth: 220 }}>
                            “{r.application_note.length > 80 ? `${r.application_note.slice(0, 80)}…` : r.application_note}”
                          </div>
                        )}
                      </td>
                      <td>
                        <div>{r.contact_name}</div>
                        <div className="plat-muted">{r.email}</div>
                        {r.phone && <div className="plat-muted">{r.phone}</div>}
                      </td>
                      <td className="plat-muted" style={{ fontSize: 12, maxWidth: 180 }}>{referralText}</td>
                      <td>
                        <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{r.plan || '—'}</div>
                        {r.plan_price ? <div className="plat-muted">£{Number(r.plan_price).toLocaleString()}</div> : null}
                      </td>
                      <td><span style={statusStyle(r.status)}>{r.status}</span></td>
                      <td>{fmtDate(r.trial_ends_at)}</td>
                      <td>{days !== null ? (days >= 0 ? `${days}d` : 'Expired') : '—'}</td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {r.status === 'pending' && (
                            <>
                              <button type="button" className="plat-btn plat-btn-primary plat-btn-sm" disabled={!!busy} onClick={() => approve(r.id)}>
                                {isBusy('approve') ? '…' : 'Approve demo'}
                              </button>
                              <button type="button" className="plat-btn plat-btn-danger plat-btn-sm" disabled={!!busy} onClick={() => run(r.id, 'reject')}>
                                {isBusy('reject') ? '…' : 'Reject'}
                              </button>
                            </>
                          )}
                          {r.status === 'trial' && (
                            <>
                              <button type="button" className="plat-btn plat-btn-primary plat-btn-sm" disabled={!!busy} onClick={() => run(r.id, 'activate')}>
                                {isBusy('activate') ? '…' : 'Activate'}
                              </button>
                              <button type="button" className="plat-btn plat-btn-ghost plat-btn-sm" disabled={!!busy} onClick={() => run(r.id, 'extend')}>
                                {isBusy('extend') ? '…' : '+7d'}
                              </button>
                            </>
                          )}
                          {(r.status === 'trial' || r.status === 'paid' || r.status === 'payment_sent') && (
                            <button type="button" className="plat-btn plat-btn-ghost plat-btn-sm" disabled={!!busy} onClick={() => paymentLink(r.id)}>
                              {isBusy('pay') ? '…' : 'Pay link'}
                            </button>
                          )}
                          {(r.status === 'trial' || r.status === 'active') && (
                            <button type="button" className="plat-btn plat-btn-danger plat-btn-sm" disabled={!!busy} onClick={() => run(r.id, 'cancel')}>
                              {isBusy('cancel') ? '…' : 'Cancel'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default ApplicationsPage;
